import React, { useEffect } from "react";
import { useLabels } from "../../../hooks/useLabels";
import { useUserEvents } from "../../../hooks/useUserEvents";
import { SimpleDialog } from "../../dialog/simple-dialog";

import { labels } from "./labels";
import { UserDateForm } from "./user-date-form";

import { useCalendarDataSources } from "../../../hooks/useCalendarDataSources";
import { TabButton, TabPanel, Tabs } from "../../tabs";
import { TabList } from "../../tabs/tab-list";
import { Footer } from "./footer";
import { SpecialDayList } from "./special-day-list";
import {
	SpecialDay,
	useUserDateDialogActions,
	useUserDateDialogState,
} from "./use-user-date-dialog-state";
import { UserDateList } from "./user-date-list";

import "./events-dialog.css";
import { useAccount } from "../../../hooks/useAccount";
import { Alert } from "../../alert/alert";
import { EventsDialogLogin } from "./events-dialog-login";

export const EventsDialog = ({
	open,
	onClose,
}: {
	open: boolean;
	onClose(results: eXpress.creator.calendars.ICalendarDataSources | undefined): void;
}) => {
	const account = useAccount();
	const [[infoText, titleText, personalEventsTabText, specialEventsTabText], areLabelsLoading] =
		useLabels([
			labels.checkedEvents,
			labels.title,
			labels.personalEventsTitle,
			labels.specialEventsTitle,
		]);

	const { data: accountUserDates } = useUserEvents();
	const { personalEvents: definitionUserDates } = useCalendarDataSources() || {};
	const {
		addUserDates: addPopupUserDates,
		clearForm,
		clear,
		updateSpecialDays,
	} = useUserDateDialogActions();
	const { specialDays } = useUserDateDialogState();

	useEffect(() => {
		// clear form data from previous dialog interactions
		clear();
		clearForm();
	}, [clearForm, clear]);

	const { specialDays: specialDaysFromDefinition } = useCalendarDataSources() || {};
	useEffect(() => {
		if (specialDaysFromDefinition) {
			updateSpecialDays(
				Object.entries(specialDaysFromDefinition).map(([key, { show }]) => ({
					key: key as SpecialDay,
					show,
				})),
			);
		}
	}, [specialDaysFromDefinition, updateSpecialDays]);

	useEffect(() => {
		if (accountUserDates && definitionUserDates) {
			addPopupUserDates([
				// add dates from account first
				...accountUserDates,
				// then merge user dates from definition
				...definitionUserDates,
			]);
		}
	}, [accountUserDates, addPopupUserDates, definitionUserDates]);

	if (areLabelsLoading) return null;

	return (
		<SimpleDialog
			title={titleText}
			isOpen={open}
			footer={<Footer closeDialog={onClose} />}
			fullScreen
		>
			<div className="events-dialog">
				<Tabs defaultValue="personal-events">
					<TabList>
						<TabButton iconName="calendar-star" value="personal-events">
							{personalEventsTabText}
						</TabButton>
						{!!specialDays.size && (
							<TabButton iconName="calendar-day" value="special-events">
								{specialEventsTabText}
							</TabButton>
						)}
					</TabList>

					<TabPanel value="personal-events" className="events-dialog__panel">
						<div className="events-dialog__header">
							<Alert iconName="info">{infoText}</Alert>
							<UserDateForm />
						</div>
						<UserDateList />
						{account?.type !== eXpress.core.AccountType.Full && <EventsDialogLogin />}
					</TabPanel>

					<TabPanel value="special-events">
						<SpecialDayList />
					</TabPanel>
				</Tabs>
			</div>
		</SimpleDialog>
	);
};
